import { startAuthentication } from "@simplewebauthn/browser";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import logoInk from "@mivio/ui/assets/mivio-wordmark.svg?url";
import { rootApi, errorText, type Me, type SecondFactor, type SignInStep } from "../api";
import { Banner } from "../editor";
import { go, useSession } from "../session";

/** Logging in, creating a login, resetting a password: one Mivio login for every business. */

type Step =
  | { kind: "form" }
  | { kind: "verify_email"; challenge: string; email: string }
  | { kind: "second"; challenge: string; methods: SecondFactor[]; email: string }
  | { kind: "reset"; challenge: string; email: string };

const FACTOR_TEXT: Record<SecondFactor, { label: string; hint: string }> = {
  email_code: { label: "Code from your email", hint: "We sent a 6-digit code to" },
  totp: { label: "Code from your authenticator app", hint: "Open your authenticator app and enter the 6-digit code for Mivio." },
  backup_code: { label: "Backup code", hint: "Each backup code works once." },
};

/** Only an address on this site: never somewhere else after logging in. */
export function safeReturn(to: string | null): string {
  return to && to.startsWith("/") && !to.startsWith("//") && !to.startsWith("/sign-") ? to : "/";
}

export function AuthCard({ title, sub, children }: { title: string; sub?: ReactNode; children: ReactNode }) {
  return (
    <div className="signin">
      <div className="signin__card">
        <div className="signin__brand">
          <img src={logoInk} alt="Mivio" width="92" height="22" />
        </div>
        <h1>{title}</h1>
        {sub ? <p>{sub}</p> : null}
        {children}
        <p className="signin__tagline">One Mivio login for every business you work with.</p>
      </div>
    </div>
  );
}

function Input({ label, ...rest }: { label: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="field">
      <span>{label}</span>
      <input {...rest} />
    </label>
  );
}

function useFinish() {
  const session = useSession();
  const [params] = useSearchParams();
  return (me: Me) => {
    session.signedIn(me);
    // A full load: the business the person lands in starts its own router.
    go(safeReturn(params.get("return")));
  };
}

function CodeStep({ title, sub, label, challenge, canResend, onSubmit, extra, submitText = "Continue" }: {
  title: string;
  sub: string;
  label: string;
  challenge: string;
  canResend: boolean;
  onSubmit: (code: string) => Promise<void>;
  extra?: ReactNode;
  submitText?: string;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(code.trim());
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };
  const resend = async () => {
    setError(null);
    try {
      await rootApi("/auth/resend-code", { method: "POST", json: { challenge } });
      setNotice("We sent a new code. It can take a minute to arrive.");
    } catch (err) {
      setError(errorText(err));
    }
  };
  return (
    <AuthCard title={title} sub={sub}>
      <form onSubmit={submit}>
        {notice ? <Banner tone="success">{notice}</Banner> : null}
        {error ? <p className="error" role="alert">{error}</p> : null}
        <Input label={label} value={code} onChange={(e) => setCode(e.target.value)} inputMode="text" autoComplete="one-time-code" autoCapitalize="none" spellCheck={false} autoFocus required />
        {extra}
        <button type="submit" className="btn btn--accent btn--lg btn--block" disabled={busy || !code.trim()}>
          {busy ? "Checking…" : submitText}
        </button>
        {canResend ? (
          <button type="button" className="signin__plain" onClick={() => void resend()}>
            Send a new code
          </button>
        ) : null}
      </form>
    </AuthCard>
  );
}

export function SignInPage() {
  const finish = useFinish();
  const [params] = useSearchParams();
  const [step, setStep] = useState<Step>({ kind: "form" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<SecondFactor | null>(null);
  const [trust, setTrust] = useState(false);
  const back = params.get("return");
  const reset = params.get("reset");

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await rootApi<SignInStep>("/auth/sign-in", { method: "POST", json: { email, password } });
      if (r.next === "done") return finish(r);
      setPassword("");
      if (r.next === "verify_email") return setStep({ kind: "verify_email", challenge: r.challenge, email });
      setMethod(r.methods[0] ?? null);
      setStep({ kind: "second", challenge: r.challenge, methods: r.methods, email });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const passkey = async () => {
    setError(null);
    try {
      const { challenge, options } = await rootApi<{ challenge: string; options: Parameters<typeof startAuthentication>[0]["optionsJSON"] }>("/auth/passkey/options", { method: "POST" });
      const response = await startAuthentication({ optionsJSON: options });
      finish(await rootApi<Me>("/auth/passkey/sign-in", { method: "POST", json: { challenge, response } }));
    } catch (err) {
      if (err instanceof Error && (err.name === "NotAllowedError" || err.name === "AbortError")) return;
      setError(errorText(err));
    }
  };

  if (step.kind === "verify_email") {
    return (
      <CodeStep
        title="Confirm your email"
        sub={`We sent a 6-digit code to ${step.email}.`}
        label="Code"
        challenge={step.challenge}
        canResend
        onSubmit={async (code) => finish(await rootApi<Me>("/auth/verify-email", { method: "POST", json: { challenge: step.challenge, code } }))}
      />
    );
  }

  if (step.kind === "second" && method) {
    const text = FACTOR_TEXT[method];
    const others = step.methods.filter((m) => m !== method);
    return (
      <CodeStep
        key={method}
        title="Two-step sign-in"
        sub={method === "email_code" ? `${text.hint} ${step.email}.` : text.hint}
        label={text.label}
        challenge={step.challenge}
        canResend={method === "email_code"}
        onSubmit={async (code) => finish(await rootApi<Me>("/auth/second-step", { method: "POST", json: { challenge: step.challenge, method, code, trustBrowser: trust } }))}
        extra={
          <>
            <label className="check-line">
              <input type="checkbox" checked={trust} onChange={(e) => setTrust(e.target.checked)} />
              <span>Trust this browser for 30 days</span>
            </label>
            {others.map((m) => (
              <button key={m} type="button" className="signin__plain" onClick={() => setMethod(m)}>
                {m === "backup_code" ? "Use a backup code instead" : m === "totp" ? "Use your authenticator app" : "Use an email code"}
              </button>
            ))}
          </>
        }
      />
    );
  }

  const keep = back ? `?return=${encodeURIComponent(back)}` : "";
  return (
    <AuthCard title="Log in" sub="Continue to Mivio.">
      <form onSubmit={submit}>
        {reset ? <Banner tone="success">Password reset. Log in with your new password.</Banner> : null}
        {error ? <p className="error" role="alert">{error}</p> : null}
        <Input label="Email" type="email" name="email" autoComplete="username webauthn" autoCapitalize="none" spellCheck={false} required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label="Password" type="password" name="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        <button type="submit" className="btn btn--accent btn--lg btn--block" disabled={busy || !email || !password}>
          {busy ? "Logging in…" : "Log in"}
        </button>
        <div className="signin__or">or</div>
        <button type="button" className="btn btn--lg btn--block" onClick={() => void passkey()}>
          Log in with a passkey
        </button>
        <div className="signin__links">
          <Link to={`/forgot-password${keep}`}>Forgot password?</Link>
          <Link to={`/sign-up${keep}`}>Create a Mivio login</Link>
        </div>
      </form>
    </AuthCard>
  );
}

export function SignUpPage() {
  const finish = useFinish();
  const [params] = useSearchParams();
  const invite = params.get("invite");
  const [step, setStep] = useState<Step>({ kind: "form" });
  const [form, setForm] = useState({ name: "", email: params.get("email") ?? "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ field?: string; message: string } | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await rootApi<{ challenge: string }>("/auth/sign-up", {
        method: "POST",
        json: {
          ...form,
          ...(invite ? { invite } : {}),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          languages: [...(navigator.languages ?? [navigator.language])].slice(0, 5),
        },
      });
      setStep({ kind: "verify_email", challenge: r.challenge, email: form.email.trim() });
    } catch (err) {
      const field = (err as { details?: { field?: string } }).details?.field;
      setError({ ...(field ? { field } : {}), message: errorText(err) });
    } finally {
      setBusy(false);
    }
  };

  if (step.kind === "verify_email") {
    return (
      <CodeStep
        title="Confirm your email"
        sub={`We sent a 6-digit code to ${step.email}.`}
        label="Code"
        challenge={step.challenge}
        canResend
        onSubmit={async (code) => finish(await rootApi<Me>("/auth/verify-email", { method: "POST", json: { challenge: step.challenge, code } }))}
      />
    );
  }

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });
  const fieldError = (f: string) => (error?.field === f ? <p className="field__error" role="alert">{error.message}</p> : null);
  const back = params.get("return");
  return (
    <AuthCard title="Create your Mivio login" sub="One login for every business you work with.">
      <form onSubmit={submit}>
        {error && !error.field ? <p className="error" role="alert">{error.message}</p> : null}
        <Input label="Your name" autoComplete="name" required autoFocus value={form.name} onChange={set("name")} />
        <Input label="Email" type="email" autoComplete="email" autoCapitalize="none" spellCheck={false} required value={form.email} onChange={set("email")} />
        {fieldError("email")}
        <Input label="Password" type="password" autoComplete="new-password" required minLength={10} value={form.password} onChange={set("password")} />
        {fieldError("password") ?? <p className="field__help">At least 10 characters. A short phrase works well.</p>}
        <button type="submit" className="btn btn--accent btn--lg btn--block" disabled={busy}>
          {busy ? "Creating…" : "Create login"}
        </button>
        <div className="signin__links">
          <Link to={`/sign-in${back ? `?return=${encodeURIComponent(back)}` : ""}`}>I already have a login</Link>
        </div>
      </form>
    </AuthCard>
  );
}

export function ForgotPasswordPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>({ kind: "form" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await rootApi<{ challenge: string }>("/auth/forgot-password", { method: "POST", json: { email } });
      setStep({ kind: "reset", challenge: r.challenge, email });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (step.kind === "reset") {
    return (
      <CodeStep
        title="Reset your password"
        sub={`If ${step.email} has a Mivio login, we sent it a 6-digit code.`}
        label="Code"
        challenge={step.challenge}
        canResend
        submitText="Set new password"
        extra={<Input label="New password" type="password" autoComplete="new-password" required minLength={10} value={password} onChange={(e) => setPassword(e.target.value)} />}
        onSubmit={async (code) => {
          await rootApi("/auth/reset-password", { method: "POST", json: { challenge: step.challenge, code, password } });
          nav("/sign-in?reset=1", { replace: true });
        }}
      />
    );
  }

  return (
    <AuthCard title="Forgot your password?" sub="We'll email you a code to set a new one.">
      <form onSubmit={submit}>
        {error ? <p className="error" role="alert">{error}</p> : null}
        <Input label="Email" type="email" autoComplete="username" autoCapitalize="none" spellCheck={false} required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
        <button type="submit" className="btn btn--accent btn--lg btn--block" disabled={busy || !email}>
          {busy ? "Sending…" : "Send code"}
        </button>
        <div className="signin__links">
          <Link to="/sign-in">Back to log in</Link>
        </div>
      </form>
    </AuthCard>
  );
}
